import 'locales';
import { createSafeContext } from '@sgme/react';
import type { IntlKey, LanguageIsoCode } from 'locales';
import { defaultLanguage, getMessages } from 'locales';
import { FC, FunctionComponent, PropsWithChildren, type ReactNode } from 'react';
import type { FormatDateOptions, IntlShape } from 'react-intl';
import { IntlProvider, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { getUserPreferencesLanguage } from 'state/selectors';
import type { AppState } from 'state/state.model';

export type SPIntl = IntlShape & {
  formatDisplayedDate: IntlShape['formatDate'];
  formatMessages: (arg: { fallbackIds: string[]; defaultMessage: string }) => string;
  idExists: (id?: unknown) => id is IntlKey;
};
export type MessageValue = string | number | boolean | null | undefined | Date;
export type SPInjectedIntlProps = {
  intl: SPIntl;
};

const dateFormatOptions: FormatDateOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
};

function buildFormatDisplayedDate(originIntl: IntlShape): IntlShape['formatDate'] {
  return (date, options) => originIntl.formatDate(date, { ...dateFormatOptions, ...options });
}

function buildIntl(originIntl: IntlShape): SPIntl {
  const formatDisplayedDate = buildFormatDisplayedDate(originIntl);
  function idExists(id?: unknown): id is IntlKey {
    return originIntl.messages[id as IntlKey] !== undefined;
  }

  const formatMessages = (arg: { fallbackIds: string[]; defaultMessage: string }) => {
    for (const key of arg.fallbackIds) {
      const message = originIntl.formatMessage({ id: key as IntlKey, defaultMessage: key });
      if (message !== key) {
        return message;
      }
    }
    return arg.defaultMessage;
  };
  return {
    ...originIntl,
    formatMessages,
    idExists,
    formatDisplayedDate,
  };
}

const WithIntlContextProvider: FC<PropsWithChildren<{}>> = ({ children }) => {
  const intl = useIntl();
  return <IntlContextProvider value={buildIntl(intl)}>{children}</IntlContextProvider>;
};

export const [IntlContextProvider, useIntlContext] = createSafeContext<SPIntl>('SpIntlContext');
interface IntlOwnProps {
  children: ReactNode;
  messages?: Record<IntlKey, string>;
}
interface IntlStateProps {
  locale: LanguageIsoCode;
}
function mapStateToIntlProps(state: AppState): IntlStateProps {
  return { locale: getUserPreferencesLanguage(state) ?? defaultLanguage };
}

export const SPIntlProvider = ({ locale, messages, children }: IntlOwnProps & IntlStateProps) => {
  const allMessages = messages ?? getMessages(locale);
  return (
    <IntlProvider locale={locale} key={locale} messages={allMessages}>
      <WithIntlContextProvider>{children}</WithIntlContextProvider>
    </IntlProvider>
  );
};

const ConnectedSPIntlProvider = connect(mapStateToIntlProps)(SPIntlProvider);

export function withIntl<P>(
  Component: FunctionComponent<PropsWithChildren<P>>,
): FunctionComponent<PropsWithChildren<P>> {
  return props => (
    <ConnectedSPIntlProvider>
      <Component {...props} />
    </ConnectedSPIntlProvider>
  );
}
